import { lazy, Suspense, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Box } from '@mui/material';

import theme, { tokens } from './theme';
import TopNav from './components/TopNav';
import Footer from './components/Footer';
import ConsentBanner from './components/ConsentBanner';
import Hero from './components/Hero';
import ProofStrip from './components/ProofStrip';
import About from './pages/About';
import Services from './pages/Services';
import Process from './pages/Process';
import Contact from './pages/Contact';
import { flushEmotionStyles } from './lib/flushEmotionStyles';

const Privacy = lazy(() => import('./pages/Privacy'));
const BlogIndex = lazy(() => import('./pages/BlogIndex'));
const BlogPost = lazy(() => import('./pages/BlogPost'));

const HomePage = () => {
  useEffect(() => {
    flushEmotionStyles();
    document.dispatchEvent(new Event('render-event'));
  }, []);

  return (
    <>
      <Hero />
      <ProofStrip />
      <Box id="about" sx={{ scrollMarginTop: '70px' }}>
        <About />
      </Box>
      <Box id="services" sx={{ scrollMarginTop: '70px' }}>
        <Services />
      </Box>
      <Box id="process" sx={{ scrollMarginTop: '70px' }}>
        <Process />
      </Box>
      <Box id="contact" sx={{ scrollMarginTop: '70px' }}>
        <Contact />
      </Box>
    </>
  );
};

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Helmet>
        <title>Cloud Lord &mdash; Infrastructure consultancy</title>
        <meta
          name="description"
          content="Platform engineering, cloud architecture, and AI enablement. AWS specialization, Terraform, Kubernetes, and company-wide LLM adoption, run by Tomislav Ivanović."
        />
        <meta name="keywords" content="AWS platform engineering, Terraform, Kubernetes, DevOps, cloud migration, Hetzner, Matomo analytics, GDPR, LLM enablement, AI adoption" />
        <meta property="og:title" content="Cloud Lord &mdash; Infrastructure consultancy" />
        <meta property="og:description" content="Formalize your AWS into a real platform. DevOps automation and AI enablement, run independently from Belgrade." />
        <meta property="og:type" content="website" />
      </Helmet>

      <BrowserRouter>
        <Box
          sx={{
            minHeight: '100vh',
            background: tokens.bg,
            color: tokens.fg,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <TopNav />
          <Box component="main" sx={{ flex: 1 }}>
            <Suspense fallback={<Box sx={{ minHeight: '60vh' }} />}>
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/blog" element={<BlogIndex />} />
                <Route path="/blog/:slug" element={<BlogPost />} />
              </Routes>
            </Suspense>
          </Box>
          <Footer />
          <ConsentBanner />
        </Box>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
